import React, { Component } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
} from 'recharts'
import moment from 'moment'
import styled from 'styled-components'
import _ from 'lodash'
import t from 'prop-types'

import Color from 'helpers/Color'
import { Button } from 'components/UI'

const Wrapper = styled.div`
  text-align: left;
`

class Timeline extends Component {
  constructor (props) {
    super(props)

    const {
      data,
      highlightDataKeys,
      dataKey1,
      dataKey2,
    } = props

    this.state = {
      zoomPoint1: null,
      zoomPoint2: null,
      zooming: false,
      highlightAreas: this.generateHighlightAreas(data, highlightDataKeys),
      chartData: this.generateChartData(data, dataKey1, dataKey2),
      showData1: true,
      showData2: true,
    }
  }

  componentWillReceiveProps (nextProps) {
    const {
      data,
      highlightDataKeys,
      dataKey1,
      dataKey2,
    } = nextProps

    this.setState({
      highlightAreas: this.generateHighlightAreas(data, highlightDataKeys),
      chartData: this.generateChartData(data, dataKey1, dataKey2),
    })
  }

  // We'll use the value associated to the given keys to highlight
  // the line graph. Positive values will render a highlighted area on the
  // background of the graph.
  generateHighlightAreas (data, keys) {
    let areas = []
    let x1 = null
    let x2 = null
    for (let i = 0; i < data.length; i++) {
      const value = _.sumBy(keys, key => _.parseInt(data[i][key]))
      if (value > 0) {
        if (x1 === null) x1 = i
        else x2 = i
      } else {
        if (x2 !== null) {
          areas.push(
            <ReferenceArea
              key={i}
              x1={x1}
              x2={x2}
              fillOpacity={0.25}
              fill={Color.green}
              yAxisId='left'
            />
          )
          x1 = null
          x2 = null
        } else {
          x1 = null
        }
      }
    }
    
    // If we finish the loop and still have defined margins,
    // it means we have one last area to draw.
    if (x1 !== null && x2 !== null) {
      areas.push(
        <ReferenceArea
          key={'last'}
          x1={x1}
          x2={x2}
          fillOpacity={0.25}
          fill={Color.green}
          yAxisId='left'
        />
      )
    }
    
    return areas
  }

  generateChartData (data, key1, key2) {
    return _.filter(data.map(item => {
      const date = moment(_.parseInt(item.UTCTimestamp)).format('D MMM hA')

      return {
        name: date,
        [key1]: _.round(item[key1], 4) || null,
        [key2]: _.round(item[key2], 4) || null,
      }
    }), null)
  }

  handleToggleData1 = () => {
    this.setState({
      showData1: !this.state.showData1,
    })
  }

  handleToggleData2 = () => {
    this.setState({
      showData2: !this.state.showData2,
    })
  }

  handleChartClick = (click, data) => {
    if(!click) return
    const index = click.activeTooltipIndex

    const {
      zoomPoint1,
      zoomPoint2,
    } = this.state

    if (this.state.zooming) {
      if (zoomPoint2 === null || zoomPoint1 === zoomPoint2) return

      const reverse = index <= zoomPoint1
      const start = reverse ? zoomPoint2 : zoomPoint1
      const end = reverse ? zoomPoint1 : zoomPoint2

      this.props.onZoom(data[start].UTCTimestamp, data[end].UTCTimestamp)

      this.setState({
        zooming: false,
      })
    } else {
      this.setState({
        zoomPoint1: index,
        zoomPoint2: index,
        zooming: true,
      })
    }
  }

  handleMouseMove = data => {
    if (!data.isTooltipActive || !this.state.zooming) return

    this.setState({
      zoomPoint2: data.activeTooltipIndex,
    })
  }

  render () {
    const {
      data,
      dataKey1,
      dataKey2,
    } = this.props

    const {
      zoomPoint1,
      zoomPoint2,
      highlightAreas,
      chartData,
      showData1,
      showData2,
    } = this.state

    const dataName1 = _.startCase(dataKey1)
    const dataName2 = _.startCase(dataKey2)

    return (
      <Wrapper>
        <Button
          onClick={this.handleToggleData1}
          color={Color.darkGray}
        >
          {dataName1}
        </Button>
        <Button
          onClick={this.handleToggleData2} right
          color={Color.gray}
        >
          {dataName2}
        </Button>
        <ResponsiveContainer
          width='100%'
          height={280}
        >
          <LineChart
            data={chartData}
            onClick={click => this.handleChartClick(click, data)}
            onMouseMove={this.handleMouseMove}
          >
            <XAxis
              dataKey='name'
              tickLine={false}
              tick={ChartLabel}
            />
            <YAxis
              domain={['dataMin - 1', 'dataMax + 1']}
              axisLine={false}
              tickLine={false}
              yAxisId='left'
            />
            <YAxis
              domain={['dataMin - 1', 'dataMax + 1']}
              axisLine={false}
              tickLine={false}
              yAxisId='right'
              orientation='right'
            />
            <CartesianGrid
              strokeDasharray='3 3'
              vertical={false}
            />
            <Tooltip/>
            <ReferenceArea
              x1={zoomPoint1}
              x2={zoomPoint2}
              yAxisId='left'
            />
            {highlightAreas}
            { showData1 &&
              <Line
                dataKey={dataKey1}
                stroke={Color.black}
                type='linear'
                isAnimationActive={false}
                dot={false}
                strokeWidth={3}
                yAxisId='left'
              />
            }
            { showData2 &&
              <Line
                dataKey={dataKey2}
                stroke={Color.gray}
                type='linear'
                isAnimationActive={false}
                dot={false}
                strokeWidth={3}
                yAxisId='right'
              />
            }
          </LineChart>
        </ResponsiveContainer>
      </Wrapper>
    )
  }
}

Timeline.propTypes = {
  data: t.array.isRequired,
  dataKey1: t.string.isRequired,
  dataKey2: t.string.isRequired,
  onZoom: t.func.isRequired,
  highlightDataKeys: t.array.isRequired,
}

Timeline.defaultProps = {
  highlightDataKeys: [],
}

const ChartLabel = ({ x, y, stroke, payload }) => {
  return (
    <text
      x={x}
      y={y}
      dy={16}
      dx={-16}
      textAnchor="center"
      fontSize='10'
    >
      {payload.value}
    </text>
  );
}

export default Timeline



// WEBPACK FOOTER //
// ./src/components/UI/Chart/Timeline.jsx